export type IndianPokerReplayEvent =
  | {
      readonly eventType: "init";
      readonly userId: string;
      readonly payload?: { readonly actionId?: string };
    }
  | {
      readonly eventType: "SyncViewerState";
      readonly userId: string;
      readonly payload?: { readonly actionId?: string };
    }
  | {
      readonly eventType: "RAISE";
      readonly userId: string;
      readonly payload: {
        readonly actionId?: string;
        readonly totalBet: number;
      };
    }
  | {
      readonly eventType: "CALL" | "FOLD" | "CONFIRM_ROUND_RESULT";
      readonly userId: string;
      readonly payload?: { readonly actionId?: string };
    };

export type IndianPokerReplayEventType = IndianPokerReplayEvent["eventType"];

export interface IndianPokerEventInput {
  readonly eventType: string;
  readonly userId?: string;
  readonly payload?: Record<string, unknown>;
}

export const INDIAN_POKER_REPLAY_EVENT_TYPES = [
  "init",
  "SyncViewerState",
  "RAISE",
  "CALL",
  "FOLD",
  "CONFIRM_ROUND_RESULT",
] as const satisfies readonly IndianPokerReplayEventType[];

function requireUserId(event: IndianPokerEventInput): string {
  if (typeof event.userId === "string" && event.userId.length > 0) {
    return event.userId;
  }
  throw new Error("Indian Poker replay event is missing userId");
}

function requireTotalBet(payload: Record<string, unknown>): number {
  if (
    typeof payload.totalBet === "number" &&
    Number.isInteger(payload.totalBet)
  ) {
    return payload.totalBet;
  }
  throw new Error("Indian Poker RAISE replay event is missing totalBet");
}

export function normalizeIndianPokerReplayEventType(
  eventType: string,
): IndianPokerReplayEventType {
  switch (eventType) {
    case "init":
    case "SyncViewerState":
    case "RAISE":
    case "CALL":
    case "FOLD":
    case "CONFIRM_ROUND_RESULT":
      return eventType;
    default:
      throw new Error(`Unsupported Indian Poker event type: ${eventType}`);
  }
}

export function toIndianPokerReplayEvent(
  event: IndianPokerEventInput,
): IndianPokerReplayEvent {
  const eventType = normalizeIndianPokerReplayEventType(event.eventType);
  const userId = requireUserId(event);
  const payload = event.payload ?? {};
  const actionId =
    typeof payload.actionId === "string" ? payload.actionId : undefined;

  switch (eventType) {
    case "RAISE":
      return {
        eventType,
        userId,
        payload: {
          actionId,
          totalBet: requireTotalBet(payload),
        },
      };
    case "init":
    case "SyncViewerState":
    case "CALL":
    case "FOLD":
    case "CONFIRM_ROUND_RESULT":
      return {
        eventType,
        userId,
        payload: actionId ? { actionId } : undefined,
      };
  }
}
